import prettyBytes from 'pretty-bytes'
import { getLatestUsageValue } from '@/components/shared/analytics/utils/usage-metrics'
import { databases } from '@/config/databases'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery, useTrailingUsageWindow } from '@/lib/hooks'
import type { RecentServicesQuery } from '@/routes/home/recent-services/query.types.gen'
import type {
  DatabaseStorageQuery,
  DatabaseStorageQueryVariables,
} from '@/routes/home/recent-services/utils.types.gen'

type Company = NonNullable<RecentServicesQuery['company']>
type App = NonNullable<NonNullable<Company['apps']>['items']>[0]
type StaticSite = NonNullable<NonNullable<Company['staticSites']>['items']>[0]
type Database = NonNullable<NonNullable<Company['databases']>['items']>[0]

export const getAppInfo = (service: App) => {
  switch (service.status) {
    case 'deleting':
      return 'App is being deleted'

    case 'deploymentInProgress':
      return 'Deployment in progress'
  }

  const lastDeployment = service.lastDeployment

  if (lastDeployment) {
    if (service.source !== 'dockerImage') {
      const commitMessage = lastDeployment.commitMessage
      return (
        <div className="flex items-center gap-1">
          <code>{lastDeployment?.commitSha?.substring(0, 6)}</code>
          <span>-</span>
          <p className="max-w-[150px] md:max-w-[250px] truncate">
            {commitMessage}
          </p>
        </div>
      )
    } else {
      return <p className="truncated">{service.dockerImage ?? ''}</p>
    }
  }

  return 'Application'
}

export const getStaticSiteInfo = (service: StaticSite) => {
  switch (service.status) {
    case 'deleting':
      return 'Static site is being deleted'

    case 'deploymentInProgress':
      return 'Deployment in progress'
  }

  const deployments = service?.deployments?.items ?? []
  const [lastDeployment] =
    deployments
      ?.slice()
      ?.sort((a, b) => a?.updatedAt?.getTime?.() - b?.updatedAt?.getTime?.()) ??
    []

  if (lastDeployment) {
    const commitMessage = lastDeployment.commitMessage
    return (
      <div className="flex items-center gap-1">
        <code>{lastDeployment?.commitSha?.substring(0, 6)}</code>
        <span>-</span>
        <p className="max-w-[150px] md:max-w-[250px] truncate">
          {commitMessage}
        </p>
      </div>
    )
  }
  return 'Static site'
}

const STORAGE_QUERY = gql`
  query DatabaseStorage($name: String!, $input: UsageMetricsInput!) {
    database(name: $name) {
      id
      displayName
      usageMetrics(input: $input) {
        series {
          name
          points {
            timestamp
            value
          }
        }
      }
    }
  }
`

export const DatabaseInfo = ({ service }: { service: Database }) => {
  const usageWindow = useTrailingUsageWindow()
  const { error, data } = useSuspenseQuery<
    DatabaseStorageQuery,
    DatabaseStorageQueryVariables
  >(STORAGE_QUERY, {
    variables: {
      name: service.name,
      input: { metrics: ['STORAGE_USAGE'], ...usageWindow },
    },
  })

  const usedStorage = getLatestUsageValue(
    data?.database?.usageMetrics?.series,
    'STORAGE_USAGE'
  )

  switch (service.status) {
    case 'creating':
      return 'Database is being created'

    case 'deleting':
      return 'Database is being deleted'

    case 'updating':
      return 'Database is being updated'

    case 'passwordChange':
      return 'Database password is being changed'

    case 'restoring':
      return 'Database is being restored'
  }

  return (
    <div className="flex items-center gap-1">
      <span>{databases[service.type].name}</span>
      <span>-</span>
      <code>
        {error || usedStorage === null ? '—' : prettyBytes(usedStorage)}/
        {service.storageSize / 1000} GB
      </code>
    </div>
  )
}
